import { useState, useCallback } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { QueryGroup, QuerySource } from '../../types/QueryBuilder';
import { convertGroupsToBackendFormat } from '../../utils/queryBuilderUtils';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';

interface SaveQueryParams {
    description: string;
    groups: QueryGroup[];
    groupId?: number | null;
    source?: QuerySource;
}

interface SaveQueryResult {
    queryId: number;
}

export const useSaveQuery = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const saveQuery = useCallback(async ({ description, groups, groupId, source }: SaveQueryParams): Promise<SaveQueryResult | null> => {
        if (!isReady || !groups?.length || !groups.some(g => g.rules?.length)) return null;

        setLoading(true);
        setError(null);

        try {
            const response = await authFetch('/saveQuery', {
                method: 'POST',
                body: JSON.stringify({
                    query_id: 0,
                    queryName: description,
                    description,
                    query_type: 1,
                    source: source ?? 'SIMPLE',
                    conditions: [convertGroupsToBackendFormat(groups)],
                    ...(groupId && groupId > 0 && { group_query_id: groupId }),
                }),
            });

            if (!response?.queryId) throw new Error('Réponse invalide du serveur');

            cache?.invalidate('saved_queries|');
            return { queryId: Number(response.queryId) };
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cache]);

    return { saveQuery, loading, error };
};

